import React, { useState, useEffect } from 'react';
import {
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
Paper,
TextField,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Tooltip,
Grid,
Typography,
Pagination,
Slide,
Fade,
InputAdornment,
Snackbar,
Alert,
Box,
CircularProgress,
Chip
} from '@mui/material';
import {
Add as AddIcon,
Edit as EditIcon,
Delete as DeleteIcon,
Search as SearchIcon,
Refresh as RefreshIcon,
Visibility as ViewIcon,
Work as WorkIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../utilities/Text';
import { APICareerList, APICareerStore, APICareerUpdate, APICareerDelete, headers } from '../utilities/APIs';
import useCustomToast from '../hooks/useToast';
import axios from 'axios';
import { formatNumberWithComma } from '../utilities/Functions';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../utilities/Permission';

const Jobs = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State
const [loading, setLoading] = useState(true);
const [openAdd, setOpenAdd] = useState(false);
const [openEdit, setOpenEdit] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [currentJob, setCurrentJob] = useState(null);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [searchTerm, setSearchTerm] = useState('');
const [error, setError] = useState(null);
const [postStatus, setPostStatus] = useState('ADD');
const [updateId, setUpdateId] = useState('');
const showToast = useCustomToast();
const [pageState, setPageState] = useState({
    pageData: [],
});
const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
});

// Form data - only name
const [formData, setFormData] = useState({
    name: ''
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

// Helper function
const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const response = await axios.get(APICareerList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];
    
    setPageState(prev => ({
        ...prev,
        pageData: data,
    }));
    } catch (error) {
    let errorMessage = "Failed to load job data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

// Filter jobs based on search term
const filteredJobs = pageState.pageData.filter(job => {
    return job.name.toLowerCase().includes(searchTerm.toLowerCase());
});

// Pagination
const count = Math.ceil(filteredJobs.length / rowsPerPage);
const indexOfLastRow = page * rowsPerPage;
const indexOfFirstRow = indexOfLastRow - rowsPerPage;
const currentRows = filteredJobs.slice(indexOfFirstRow, indexOfLastRow);

const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
};

// Form handling
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Dialog handlers
const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    name: ''
    });
    setOpenAdd(true);
};

const handleOpenView = (job) => {
    setCurrentJob(job);
    setOpenView(true);
};

const handleOpenEdit = (job) => {
    setFormData({
    name: job.name
    });
    setCurrentJob(job);
    setUpdateId(job.id);
    setPostStatus("UPDATE");
    setOpenEdit(true);
};

const handleOpenDelete = (job) => {
    setCurrentJob(job);
    setUpdateId(job.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

// Prevent closing when clicking backdrop (outside dialog)
const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
    return;
    }
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenView(false);
    setOpenDelete(false);
};

const handleRefresh = () => {
    setLoading(true);
    fetchData();
    setSnackbar({ open: true, message: 'Job data refreshed', severity: 'info' });
};

const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
};

const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push('Job name is required');

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }
    
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('updateId', updateId);

    try {
    let postRequest;
    if (postStatus === "ADD") {
        postRequest = await axios.post(APICareerStore, formDataToSend, { headers });
    } else if (postStatus === "UPDATE") {
        postRequest = await axios.post(APICareerUpdate, formDataToSend, { headers });
    } else if (postStatus === "DELETE") {
        postRequest = await axios.post(APICareerDelete, formDataToSend, { headers });
    }

    const response = postRequest.data.status;
    const message = postRequest.data.message;

    if (response === "success") {
        handleClose();
        fetchData();
        showToast("Success", message, "success");
    } else {
        showToast("Error", "Unknown Server Error, Record Not Created", "error");
    }
    } catch (error) {
    if (error.response) {
        if (error.response.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = [];

        for (const field in errors) {
            errorMessages.push(...errors[field]);
        }

        showToast(
            "Validation Error",
            errorMessages.join("\n"),
            "error"
        );
        } else {
        showToast(
            "Error",
            error.response.data.message || "An error occurred",
            "error"
        );
        }
    } else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
    } else {
        showToast("Error", "An unexpected error occurred", "error");
    }
    }
};

// Loading component
if (loading) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <CircularProgress />
        <Typography variant="body1">Loading jobs...</Typography>
    </Box>
    );
}

// Error component
if (error) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <Typography variant="h6" color="error">
        Error Loading Data
        </Typography>
        <Typography variant="body2" color="textSecondary">
        {error}
        </Typography>
        <Button 
        variant="contained" 
        color="primary" 
        onClick={fetchData}
        startIcon={<RefreshIcon />}
        >
        Retry
        </Button>
    </Box>
    );
}

return (
    <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Paper elevation={3} style={{
        padding: isMobile ? '16px' : '24px',
        borderRadius: '12px',
        marginBottom: '24px'
        }}>
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item>
            <Typography variant="subtitle1">
                Jobs Total:
                <Chip
                label={formatNumberWithComma(pageState.pageData.length)} 
                color="primary" 
                variant="outlined" 
                style={{ marginLeft: '8px' }}
                />
            </Typography>
            </Grid>
            <Grid item>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                style={{ borderRadius: '8px' }}
            >
                Add Job
            </Button>
            </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
                }}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                    <SearchIcon />
                    </InputAdornment>
                ),
                style: { borderRadius: '8px' }
                }}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                variant="outlined"
                style={{ borderRadius: '8px' }}
                SelectProps={{
                native: true,
                }}
            >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
            </TextField>
            </Grid>
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Tooltip title="Refresh data">
                <IconButton
                onClick={handleRefresh}
                style={{ backgroundColor: theme.palette.grey[200], borderRadius: '8px' }}
                >
                <RefreshIcon />
                </IconButton>
            </Tooltip>
            </Grid>
        </Grid>
        </Paper>
    </Slide>

    <Fade in={!loading} timeout={500}>
        <div>
        <TableContainer component={Paper} elevation={3} style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <Table>
            <TableHead>
                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Job Name</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {currentRows.length > 0 ? (
                currentRows.map((job, index) => (
                    <StyledTableRow key={job.id}>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkIcon color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                            {job.name}
                        </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Tooltip title="View Details">
                            <ActionButton
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={() => handleOpenView(job)}
                            style={{ backgroundColor: '#17a2b8' }}
                            >
                            <ViewIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        {hasPermission(PERMISSIONS.ALL) && (
                            <>
                            <Tooltip title="Edit">
                                <ActionButton
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOpenEdit(job)}
                                >
                                <EditIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <ActionButton
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleOpenDelete(job)}
                                >
                                <DeleteIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            </>
                        )}
                        </div>
                    </TableCell>
                    </StyledTableRow>
                ))
                ) : (
                <TableRow>
                    <TableCell colSpan={3} style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="subtitle1">
                        {searchTerm ? 'No matching jobs found' : 'No job records available'}
                    </Typography>
                    {!searchTerm && (
                        <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        style={{ marginTop: '16px' }}
                        >
                        Add First Job
                        </Button>
                    )}
                    </TableCell>
                </TableRow>
                )}
            </TableBody>
            </Table>
        </TableContainer>

        {filteredJobs.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
                count={count}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
            />
            </div>
        )}
        </div>
    </Fade>

    {/* Add Job Dialog */}
    <Dialog 
        open={openAdd} 
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Add New Job</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Job Name"
                name="name"
                value={formData.name}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter job position name"
                />
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Save Job
        </Button>
        </DialogActions>
    </Dialog>

    {/* View Job Dialog */}
    <Dialog
        open={openView}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Job Details</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        {currentJob && (
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WorkIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Job Name:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentJob.name}
                </Typography>
            </Grid>
            </Grid>
        )}
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="primary" variant="contained">
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Edit Job Dialog */}
    <Dialog
        open={openEdit}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Edit Job</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Job Name"
                name="name"
                value={formData.name}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                />
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Update Job
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
        <Typography variant="body1" style={{ marginBottom: '16px' }}>
            Are you sure you want to delete this job?
        </Typography>
        <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
            {currentJob?.name}
        </Typography>
        <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
            This action cannot be undone.
        </Typography>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} variant="outlined" color="primary">
            Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Delete
        </Button>
        </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
    >
        <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
        >
        {snackbar.message}
        </Alert>
    </Snackbar>
    </div>
);
};

export default Jobs;

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
'&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
},
'&:hover': {
    backgroundColor: theme.palette.action.selected,
    transition: 'background-color 0.3s ease',
},
}));

const ActionButton = styled(Button)(({ theme }) => ({
margin: theme.spacing(0.5),
minWidth: 'auto',
}));